import { makeAutoObservable, runInAction } from 'mobx';
import moment from 'moment-timezone';

//import { encodeObjectToBase64Url, decodeBase64UrlToObject } from 'utils/filtersEncoder';
import { rideListStore } from 'pages/RideListPage/RideListStore';
import { resetTimestampToUtc, updateDateWithTime } from 'utils/datetime';

import {
	ALLOWED_DEADHEAD_FILTER_TYPES,
	DEPARTURE_RANGE_FILTER_KEY,
	EMPTY_VALUES,
	LOCAL_STORAGE_SOURCE,
	RUN_NAMES_FILTER_KEY,
	TIME_FILTER_KEY,
} from './filterConstants';
import { getIdValue, getValueToPush } from './utils/rideFiltersUtils';

class RideFilterStore {
	filtersLoaded = false;
	rideId = '';
	deadheadId = '';
	filtersDefault = EMPTY_VALUES;
	filters = EMPTY_VALUES;
	filtersMap = new Map();

	constructor() {
		makeAutoObservable(this);
	}

	setFilters(formdata) {
		runInAction(() => {
			Object.entries(formdata).forEach(([key, value]) => {
				if (value === null || value === undefined || value === '') {
					return;
				}
				if (key === DEPARTURE_RANGE_FILTER_KEY || key === TIME_FILTER_KEY) {
					return;
				}
				const id = `${key}${Object.hasOwn(value, 'key') ? value.key : value}`;
				if (this.filtersMap.has(id)) {
					return;
				}
				this.filtersMap.set(id, { value, type: key });
			});
			this.handleDateRangeChange(formdata);
		});
		this.syncLocalStorage(this.filtersMap);
		// const encoded = encodeObjectToBase64Url(this.filters);
		// localStorage.setItem('rideFilters', encoded);
		//window.history.replaceState({}, '', `${window.location.pathname}?filters=${encoded}`);
	}

	handleDateRangeChange = (departureRange, time = '00:00') => {
		const isSameDay = departureRange.from === departureRange.to;
		const timeEnd = '23:59';
		const newFrom = updateDateWithTime(departureRange.from, time);
		const newTo = updateDateWithTime(departureRange.to, isSameDay ? timeEnd : time);
		const { from, to } = this.filtersMap.get(DEPARTURE_RANGE_FILTER_KEY)?.value || {};
		if (newFrom === from && newTo === to) {
			return;
		}
		runInAction(() => {
			this.filtersMap.set(DEPARTURE_RANGE_FILTER_KEY, {
				value: { from: newFrom, to: newTo },
				type: DEPARTURE_RANGE_FILTER_KEY,
			});
			this.isOneDay = isSameDay;
		});

		this.syncLocalStorage(this.filtersMap);
	};

	checkFilterOnMount = () => {
		const storedMap = localStorage.getItem(LOCAL_STORAGE_SOURCE);
		if (storedMap) {
			const entries = JSON.parse(storedMap);
			const filtersMap = new Map();

			entries.forEach(([id, filter]) => {
				if (filter.type === RUN_NAMES_FILTER_KEY) {
					const caseSensitiveId = `${filter.type}${filter.value}`;
					filtersMap.set(caseSensitiveId, filter);
				} else {
					filtersMap.set(id, filter);
				}
			});

			runInAction(() => {
				this.filtersMap = filtersMap;
			});
		}

		runInAction(() => {
			this.filtersLoaded = true;
		});
	};

	// checkFilterOnMount = async () => {
	// 	// const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
	// 	// await wait(0); // Wait for next tick to ensure URL is fully loaded
	// 	// const urlParams = new URLSearchParams(window.location.search);
	// 	// const filtersParam = urlParams.get('filters');

	// 	// if (filtersParam) {
	// 	// 	console.log('Filters found in URL:', filtersParam);
	// 	// 	const decodedFilters = decodeBase64UrlToObject(filtersParam);
	// 	// 	if (decodedFilters && Object.keys(decodedFilters).length > 0) {
	// 	// 		filtersToSet = decodedFilters;
	// 	// 		return;
	// 	// 	}
	// 	// }

	// 	// If no filters in URL, check localStorage
	// 	// if (!filtersToSet) {
	// 	// 	console.log('No filters found in URL, checking localStorage...');

	// 	// }

	// 	let filtersToSet = null;

	// 	const storedFilters = localStorage.getItem('rideFilters');
	// 	const decodedStoredFilters = storedFilters ? decodeBase64UrlToObject(storedFilters) : null;
	// 	if (decodedStoredFilters && Object.keys(decodedStoredFilters).length > 0) {
	// 		// If filters are found in localStorage, set them and update URL
	// 		filtersToSet = decodedStoredFilters;
	// 		//window.history.replaceState({}, '', `${window.location.pathname}?filters=${storedFilters}`);
	// 	}

	// 	// If filters are found, set them
	// 	if (filtersToSet) {
	// 		this.filters = filtersToSet;
	// 	}
	// };

	resetUrlParamsOnExit = () => {
		// const url = new URL(window.location.href);
		// url.searchParams.delete('filters');
		// window.history.replaceState({}, '', url.pathname + url.search);
	};

	syncLocalStorage = (filtersMap) => {
		const serializedMap = JSON.stringify(Array.from(filtersMap.entries()));

		localStorage.setItem(LOCAL_STORAGE_SOURCE, serializedMap);
	};

	addFilter = (filter) => {
		const { type, value } = filter;
		if (!value) {
			return;
		}

		const idValue = getIdValue(type, value);
		const id = `${type}${idValue}`;

		if (this.filtersMap.has(id)) {
			return;
		}
		this.filtersMap.set(id, filter);

		this.syncLocalStorage(this.filtersMap);
	};

	removeFilter = (filterId) => {
		if (this.filtersMap.has(filterId)) {
			runInAction(() => {
				this.filtersMap.delete(filterId);
			});
			this.syncLocalStorage(this.filtersMap);
		}
	};

	get filterObject() {
		const accumulatedFilterObject = {};

		// Handle specific ID searches
		if (this.rideId && !rideListStore.isDeadheadsTabActive) {
			accumulatedFilterObject.rideId = this.rideId;
			return accumulatedFilterObject;
		}

		if (this.deadheadId && rideListStore.isDeadheadsTabActive) {
			accumulatedFilterObject.rideId = this.deadheadId;
			return accumulatedFilterObject;
		}

		const isDeadheadTab = rideListStore.isDeadheadsTabActive;
		const allowedDeadheadTypes = new Set(ALLOWED_DEADHEAD_FILTER_TYPES);

		[...this.filtersMap.values()].forEach((filter) => {
			const { type } = filter;

			// Skip unsupported filters for deadheads
			if (isDeadheadTab && !allowedDeadheadTypes.has(type)) return;

			if (type === DEPARTURE_RANGE_FILTER_KEY) {
				accumulatedFilterObject.departureFromDateTime = resetTimestampToUtc(filter.value.from);
				accumulatedFilterObject.departureToDateTime = resetTimestampToUtc(filter.value.to);
				return;
			}

			const targetKey = type;

			if (!accumulatedFilterObject[targetKey]) {
				accumulatedFilterObject[targetKey] = [];
			}

			const { value } = filter;
			const dataToPush = getValueToPush(value, targetKey);

			accumulatedFilterObject[targetKey].push(dataToPush);
		});

		return accumulatedFilterObject;
	}

	get resetEnabled() {
		return this.filtersMap.size > 0;
	}

	get isRideIdApplied() {
		return this.rideId !== '' || (rideListStore.isDeadheadsTabActive && this.deadheadId !== '');
	}

	setDefaultValues = () => {
		const defaultValues = {
			...EMPTY_VALUES,
			departureRange: this.filtersMap.get(DEPARTURE_RANGE_FILTER_KEY)?.value || '',
			time: this.filtersMap.get(DEPARTURE_RANGE_FILTER_KEY)?.value?.from
				? moment(this.filtersMap.get(DEPARTURE_RANGE_FILTER_KEY).value.from).format('HH:mm')
				: '',
		};
		this.filtersDefault = defaultValues;
	};

	resetFilters = () => {
		this.filtersMap.clear();
		localStorage.removeItem(LOCAL_STORAGE_SOURCE);
		// Mark filters as loaded to proceed with fetch
		this.filtersLoaded = true;
	};

	setRideId = (id) => {
		this.rideId = id;
	};

	setDeadheadId = (id) => {
		this.deadheadId = id;

		// Reset fallback state when clearing deadhead search
		if (!id) {
			rideListStore.resetFallbackState();
		}
	};
}

export const rideFilterStore = new RideFilterStore();
